import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2, Copy } from "lucide-react";

const GOOGLE_ICON = "https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp";

export default function ReviewPrompt({ slug, tableSessionId, clientSessionId, googleUrl, theme, t }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const shown = useRef(false);

  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    api.trackReviewEvent({ restaurant_slug: slug, table_session_id: tableSessionId, event: "review_prompt_shown" }).catch(() => {});
  }, [slug, tableSessionId]);

  const submit = async () => {
    if (rating < 1) { toast.error(t.reviewPickStar); return; }
    setSubmitting(true);
    try {
      await api.submitReview({
        restaurant_slug: slug, table_session_id: tableSessionId,
        client_session_id: clientSessionId, rating, comment: comment.trim() || null,
      });
      setSubmitted(true);
      toast.success(t.reviewThanks);
    } catch (e) { toast.error("Erreur lors de l'envoi de l'avis"); }
    finally { setSubmitting(false); }
  };

  const openGoogle = () => {
    api.trackReviewEvent({ restaurant_slug: slug, table_session_id: tableSessionId, event: "review_google_clicked" }).catch(() => {});
    window.open(googleUrl, "_blank", "noopener,noreferrer");
  };
  const [copied, setCopied] = useState(false);
  const copyComment = async () => {
    try { await navigator.clipboard.writeText(comment.trim()); setCopied(true); toast.success(t.reviewCopied || "Commentaire copié — collez-le sur Google"); }
    catch { toast.error("Copie impossible sur cet appareil"); }
  };

  return (
    <div data-testid="review-prompt" className="rounded-3xl bg-white border border-[#EAE5DC] p-6 text-center shadow-[0_8px_24px_rgba(18,48,38,0.05)] animate-slide-up">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#C69C4A] font-bold mb-1">{t.reviewEyebrow}</p>
      <h2 className="font-serif-display text-2xl text-[#123026] leading-tight">{t.reviewTitle}</h2>

      {!submitted ? (
        <>
          <p className="text-sm text-[#8A918D] mt-1.5">{t.reviewSubtitle}</p>
          <div className="flex justify-center gap-1.5 mt-4" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => {
              const active = (hover || rating) >= n;
              return (
                <button
                  key={n}
                  data-testid={`review-star-${n}`}
                  onMouseEnter={() => setHover(n)}
                  onClick={() => setRating(n)}
                  className="p-1 active:scale-90 transition-transform"
                  aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                >
                  <Star className="w-9 h-9 transition-colors" strokeWidth={1.5}
                    style={{ color: active ? theme.gold : "#D8D2C6", fill: active ? theme.gold : "transparent" }} />
                </button>
              );
            })}
          </div>
          <textarea
            data-testid="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t.reviewCommentPlaceholder}
            rows={3}
            className="mt-4 w-full rounded-xl border border-[#EAE5DC] px-3 py-2.5 text-sm outline-none focus:border-[#123026] resize-none"
          />
          <button
            data-testid="review-submit"
            onClick={submit}
            disabled={submitting}
            className="mt-3 w-full h-12 rounded-full text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center"
            style={{ backgroundColor: theme.primary }}
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} {t.reviewSubmit}
          </button>
        </>
      ) : (
        <div data-testid="review-submitted" className="mt-4">
          <div className="w-14 h-14 rounded-full bg-[#EBF2EE] grid place-items-center mx-auto mb-2">
            <CheckCircle2 className="w-7 h-7" style={{ color: theme.primary }} />
          </div>
          <p className="text-sm text-[#5C615E]">{t.reviewThanksLong}</p>
        </div>
      )}

      {googleUrl && (
        <>
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-[#EAE5DC]" />
            <span className="text-[11px] text-[#B4B9B4] uppercase tracking-wider">{t.reviewAnd}</span>
            <div className="flex-1 h-px bg-[#EAE5DC]" />
          </div>
          <button
            data-testid="review-google-btn"
            onClick={openGoogle}
            className="w-full h-12 rounded-full border border-[#EAE5DC] bg-white hover:bg-[#F7F5F0] text-[#123026] font-semibold text-sm flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
          >
            <img src={GOOGLE_ICON} alt="Google" className="w-5 h-5" />
            {submitted ? (t.reviewGoogleAlso || "Publier aussi sur Google") : t.reviewGoogle}
          </button>
          {submitted && comment.trim() && (
            <button data-testid="review-copy-comment" onClick={copyComment} className="mt-2 w-full h-10 rounded-full text-[#123026] text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-[#F7F5F0]">
              {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? (t.reviewCopiedShort || "Copié !") : (t.reviewCopy || "Copier mon commentaire pour Google")}
            </button>
          )}
          <p className="text-[10px] text-[#B4B9B4] mt-2">{t.reviewGoogleNote || "Vous confirmez vous-même la publication sur Google."}</p>
        </>
      )}
    </div>
  );
}
